import React, { useCallback, useState } from "react";
import _ from "lodash";
import { useUpdateNoticeApi } from "../../../../components/privacyCenter/EditPrivacyCenter";
import {
  PrivacyCenterNoticeSectionDto,
  PrivacyCenterPolicySectionDto,
} from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { Modal } from "@truevault/aquilo/src/components/modal";
import { SectionEditor } from "@truevault/aquilo/src/components/sectionEditor";
import { ApiRouteHelpers } from "../../../../../common/root/apiRoutes";
import { useSnackBar } from "@truevault/aquilo";

// Given a string like New Hampshire (NHPA), return "New Hampshire" (i.e. the part before the first parenthesis)
export const cleanStatePrivacyNoticeName = (statePrivacyNoticeName: string) => {
  return statePrivacyNoticeName.split("(")[0].trim();
};

const ModalWarningIcon = (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="24" fill="#FEF2F2" />
    <path
      d="M24.0003 20.9999V24.7499M14.697 28.1256C13.8316 29.6256 14.9142 31.4999 16.6459 31.4999H31.3546C33.0863 31.4999 34.1689 29.6256 33.3035 28.1256L25.9492 15.3781C25.0833 13.8772 22.9172 13.8772 22.0513 15.3781L14.697 28.1256ZM24.0003 27.7499H24.0078V27.7574H24.0003V27.7499Z"
      stroke="#E12037"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export async function downloadStateSectionContent(
  organizationId: string,
  privacyCenterId: string,
  noticePublicId: string,
  stateAnchor: string,
) {
  const downloadUrl =
    ApiRouteHelpers.organization.privacyCenter.notices.downloadStateSectionContent(
      organizationId,
      privacyCenterId,
      noticePublicId,
    ) + `?section=${stateAnchor}`;

  // Create a hidden <a> tag to download the notice without leaving the page
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.target = "_blank";
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();

  await new Promise<void>((resolve) => {
    setTimeout(() => {
      document.body.removeChild(link);
      resolve();
    }, 1000);
  });
}

export const USStateNoticeEditor = ({
  organizationId,
  privacyCenterId,
  noticeId,
  noticePublicId,
  editableStateSection,
  defaultStateSection,
  refreshNotice,
  setSaving,
  setIsSaved,
  onUnlock,
  onLock,
  onVisibilityToggle,
  hiddenStateSectionAnchors,
}: {
  organizationId: string;
  privacyCenterId: string;
  noticeId: string;
  noticePublicId: string;
  editableStateSection: PrivacyCenterNoticeSectionDto;
  defaultStateSection: PrivacyCenterPolicySectionDto;
  refreshNotice: () => void;
  setSaving: (saving: boolean) => void;
  setIsSaved: (isSaved: boolean) => void;
  hiddenStateSectionAnchors: string[];
  onVisibilityToggle: () => Promise<void>;
  onUnlock: () => Promise<void>;
  onLock: () => Promise<void>;
}) => {
  const enqueueSnackbar = useSnackBar();

  const { updateNotice } = useUpdateNoticeApi();

  const isEditable = editableStateSection !== null && editableStateSection !== undefined;
  const isHidden = (hiddenStateSectionAnchors || []).includes(defaultStateSection.anchor);

  const [showMakeEditableWarningModal, setShowMakeEditableWarningModal] = useState(false);

  const [showLockWarningModal, setShowLockWarningModal] = useState(false);

  const [showHideNoticeWarningModal, setShowHideNoticeWarningModal] = useState(false);

  // Starts as undefined, and only gets set if notice is edited
  const [latestContent, setLatestContent] = useState(null);

  const debouncedSave = useCallback(
    _.debounce(async (newContent: string) => {
      setSaving(true);

      let updateNoticeBody = {
        organizationId: organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: noticePublicId,
        updateNoticeBody: {
          id: noticeId,
          sections: [
            {
              id: editableStateSection.id,
              content: newContent,
            },
          ],
        },
      };

      await updateNotice(updateNoticeBody);

      setSaving(false);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);

      await refreshNotice();
    }, 750),
    [],
  );

  const content = isEditable
    ? latestContent && latestContent.length > 0
      ? latestContent
      : editableStateSection.content
    : defaultStateSection.body;

  return (
    <div id={`section-${defaultStateSection.anchor}`} style={{ marginTop: "48px", width: "100%" }}>
      <SectionEditor
        hideNavigationCheckbox={true}
        hideToolbar={true}
        addToNavigation={false}
        header={defaultStateSection.name}
        headerDisabled={true}
        content={content}
        locked={!isEditable || isHidden}
        showLockIcon={!isHidden}
        showVisibilityToggle={true}
        isVisible={!isHidden}
        onVisibilityToggle={() => {
          // Note that `isHidden` is the existing visibiliy value, i.e. when hiding show a warning modal, and when unhiding show a toast notification
          if (isHidden) {
            enqueueSnackbar(
              `The ${cleanStatePrivacyNoticeName(
                defaultStateSection.name,
              )} notice will be shown in your Privacy Center.`,
              { variant: "success" },
            );
            onVisibilityToggle();
          } else {
            setShowHideNoticeWarningModal(true);
          }
        }}
        placeholder={undefined}
        onInputChange={({ content }) => {
          if (isEditable) {
            setLatestContent(content);
            debouncedSave(content);
          }
        }}
        onUnlock={() => {
          setShowMakeEditableWarningModal(true);
        }}
        onLock={
          isEditable
            ? () => {
                setShowLockWarningModal(true);
              }
            : undefined
        }
        enablePrivacyCenterToolbar={true}
      />
      {showMakeEditableWarningModal && (
        <Modal
          title={`You are about to alter the ${cleanStatePrivacyNoticeName(
            defaultStateSection.name,
          )} Privacy Notice`}
          alignText="center"
          firstButton="Cancel"
          onFirstButtonClick={() => setShowMakeEditableWarningModal(false)}
          secondButton="I Understand"
          secondButtonColor="warning"
          onSecondButtonClick={() => {
            setShowMakeEditableWarningModal(false);
            onUnlock();
          }}
          showOverlay
          onClose={() => setShowMakeEditableWarningModal(false)}
          widthButton="full"
          alignButton="right"
          headerIcon={ModalWarningIcon}
        >
          <p className="text-center text-gray-500 text-sm">
            Our notice templates have been thoroughly researched and reviewed for compliance and
            they update dynamically when your Data Map changes.
          </p>
          <p className="mt-2 text-center text-gray-500 text-sm">
            By unlocking and changing the notice, you assume responsibility for its content. You can
            revert back to the TrueVault template at any time.
          </p>
        </Modal>
      )}
      {showLockWarningModal && (
        <RestoreStateSectionWarningModal
          closeModal={() => setShowLockWarningModal(false)}
          restoreFn={async () => {
            await downloadStateSectionContent(
              organizationId,
              privacyCenterId,
              noticePublicId,
              defaultStateSection.anchor,
            );
            onLock();
            setShowLockWarningModal(false);
          }}
        />
      )}
      {showHideNoticeWarningModal && (
        <Modal
          title={`You are about to hide the ${cleanStatePrivacyNoticeName(
            defaultStateSection.name,
          )} Privacy Notice`}
          alignText="center"
          firstButton="Cancel"
          onFirstButtonClick={() => setShowHideNoticeWarningModal(false)}
          secondButton="I Understand"
          secondButtonColor="warning"
          onSecondButtonClick={() => {
            setShowHideNoticeWarningModal(false);
            onVisibilityToggle();
          }}
          showOverlay
          onClose={() => setShowHideNoticeWarningModal(false)}
          widthButton="full"
          alignButton="right"
          headerIcon={ModalWarningIcon}
        >
          <p className="text-center text-gray-500 text-sm">
            Removing this state will also remove it from the Location selector on your Privacy
            Center Request Form. Consumers from this state will not be able to submit requests to
            your organization.
          </p>
        </Modal>
      )}
    </div>
  );
};

export const RestoreStateSectionWarningModal = ({
  restoreFn,
  closeModal,
}: {
  restoreFn: () => void;
  closeModal: () => void;
}) => {
  return (
    <Modal
      title={`Restore TrueVault notice and download copy of your notice`}
      alignText="center"
      firstButton="Cancel"
      onFirstButtonClick={closeModal}
      secondButton="Restore and download"
      secondButtonColor="warning"
      onSecondButtonClick={restoreFn}
      showOverlay
      onClose={closeModal}
      widthButton="full"
      alignButton="right"
      headerIcon={ModalWarningIcon}
    >
      <p className="text-center text-gray-500 text-sm">
        All the changes you have made to the notice will not be saved and TrueVault's locked notice
        will be restored and shown on your site. A copy of your current notice will be downloaded to
        your computer.
      </p>
    </Modal>
  );
};
